import { useEffect, useRef } from 'react';
import {
  BarController, BarElement, CategoryScale, Chart as ChartJS, LineController, LineElement, LinearScale,
  PointElement, Tooltip, type ChartConfiguration,
} from 'chart.js';
import { useTheme } from '@/hooks/useTheme';
import { tokenColor } from '@/components/Chart';
import { formatBRL, formatBRLCompact } from '@/lib/format';

ChartJS.register(
  BarController, BarElement, LineController, LineElement, PointElement, CategoryScale, LinearScale, Tooltip,
);

export interface ChartSeries {
  key: string;
  label: string;
  /** Token CSS da cor. A cor segue a série, nunca a posição dela no gráfico. */
  token: string;
  /** Colunas empilham entre si; a linha corre por cima, no mesmo eixo. */
  mark: 'bar' | 'line';
  /** Centavos, um valor por ponto. */
  values: number[];
}

interface SeriesChartProps {
  /** Rótulo curto de cada ponto, no eixo. */
  labels: string[];
  /** Nome completo de cada ponto, no tooltip e na tabela. */
  titles: string[];
  series: ChartSeries[];
  /** Nome da soma das colunas, ex.: "Vendido no dia". */
  stackTotalLabel: string;
  ariaLabel: string;
  height?: number;
}

type PointContext = { dataIndex: number };

/**
 * Gráfico de várias séries num eixo só: colunas empilhadas e, por cima, uma
 * linha. Legenda sempre à vista e a mesma informação em tabela logo abaixo —
 * o tooltip ajuda, mas nenhum número depende dele.
 */
export function SeriesChart({ labels, titles, series, stackTotalLabel, ariaLabel, height = 260 }: SeriesChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resolved: theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const surface = tokenColor('--background');
    const muted = tokenColor('--muted-foreground');
    const grid = tokenColor('--border');
    const ink = tokenColor('--popover-foreground');
    const colors = series.map((item) => tokenColor(item.token));
    const bars = series.filter((item) => item.mark === 'bar');

    // Só o segmento do topo de cada pilha ganha o canto arredondado; a base fica reta, no eixo.
    const topOfStack = labels.map((_, index) => {
      const filled = bars.filter((bar) => (bar.values[index] ?? 0) > 0);
      return filled[filled.length - 1]?.key ?? null;
    });
    const filledBelow = (barIndex: number, index: number) =>
      bars.slice(0, barIndex).some((bar) => (bar.values[index] ?? 0) > 0);

    const datasets: ChartConfiguration['data']['datasets'] = series.map((item, seriesIndex) => {
      const color = colors[seriesIndex] ?? muted;
      const data = item.values.map((cents) => cents / 100);

      if (item.mark === 'line') {
        return {
          type: 'line' as const,
          label: item.label,
          data,
          stack: item.key, // pilha própria: a linha não soma com as colunas
          borderColor: color,
          backgroundColor: color,
          borderWidth: 2,
          borderCapStyle: 'round' as const,
          borderJoinStyle: 'round' as const,
          tension: 0,
          // Marcador só no último ponto; os demais aparecem ao passar o mouse.
          pointRadius: (context: PointContext) => (context.dataIndex === data.length - 1 ? 4 : 0),
          pointHoverRadius: 5,
          pointHitRadius: 12,
          pointBackgroundColor: color,
          pointBorderColor: surface,
          pointBorderWidth: 2,
          order: 0,
        };
      }

      const barIndex = bars.indexOf(item);
      return {
        type: 'bar' as const,
        label: item.label,
        data,
        stack: 'colunas',
        backgroundColor: color,
        hoverBackgroundColor: `${color}d0`,
        // Entre segmentos empilhados, um fio de 2px na cor do fundo — no lugar de contorno.
        borderColor: surface,
        borderWidth: (context: PointContext) => ({
          top: 0, right: 0, left: 0, bottom: filledBelow(barIndex, context.dataIndex) ? 2 : 0,
        }),
        borderRadius: (context: PointContext) =>
          topOfStack[context.dataIndex] === item.key ? { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 } : 0,
        borderSkipped: false,
        maxBarThickness: 24,
        order: 1,
      };
    });

    // Quem pediu menos movimento ao sistema recebe o gráfico já desenhado, sem as colunas crescendo.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const chart = new ChartJS(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        ...(reduceMotion ? { animation: false as const } : {}),
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        font: { family: getComputedStyle(document.body).fontFamily },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tokenColor('--popover'),
            borderColor: grid,
            borderWidth: 1,
            titleColor: ink,
            bodyColor: ink,
            footerColor: ink,
            padding: 10,
            // Chave em traço curto, não em caixa: no tooltip ela só identifica.
            boxWidth: 10,
            boxHeight: 3,
            boxPadding: 6,
            itemSort: (a, b) => a.datasetIndex - b.datasetIndex,
            callbacks: {
              title: (items) => titles[items[0]?.dataIndex ?? 0] ?? '',
              label: (context) => `${formatBRL(Math.round((context.parsed.y ?? 0) * 100))} · ${context.dataset.label ?? ''}`,
              labelColor: (context) => {
                const color = colors[context.datasetIndex] ?? muted;
                return { borderColor: color, backgroundColor: color, borderWidth: 0 };
              },
              footer: (items) => {
                const total = items
                  .filter((item) => series[item.datasetIndex]?.mark === 'bar')
                  .reduce((sum, item) => sum + (item.parsed.y ?? 0), 0);
                return `${stackTotalLabel}: ${formatBRL(Math.round(total * 100))}`;
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            border: { color: grid },
            ticks: {
              color: muted,
              font: { size: 11 },
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: labels.length > 20 ? 10 : 12,
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            border: { display: false },
            grid: { color: grid },
            ticks: {
              color: muted,
              font: { size: 11 },
              maxTicksLimit: 6,
              callback: (value) => formatBRLCompact(Math.round(Number(value) * 100)),
            },
          },
        },
      },
    });

    return () => chart.destroy();
  }, [labels, titles, series, stackTotalLabel, theme]);

  // Na tabela, só os pontos com algum valor: um mês de dias zerados não diz nada.
  const rows = labels.map((_, index) => index).filter((index) => series.some((item) => (item.values[index] ?? 0) > 0));
  const stackTotal = (index: number) =>
    series.filter((item) => item.mark === 'bar').reduce((sum, item) => sum + (item.values[index] ?? 0), 0);

  return (
    <figure className="series-chart">
      <ul className="chart-legend" aria-label="Legenda do gráfico">
        {series.map((item) => (
          <li key={item.key}>
            <span
              className={`series-key${item.mark === 'line' ? ' is-line' : ''}`}
              style={{ background: `var(${item.token})` }}
              aria-hidden
            />
            {item.label}
          </li>
        ))}
      </ul>

      <div style={{ height, position: 'relative' }}>
        <canvas ref={canvasRef} role="img" aria-label={ariaLabel} />
      </div>

      <details className="chart-table">
        <summary>Ver os números em tabela</summary>
        {rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nada no período.</p>
        ) : (
          <>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th scope="col">Quando</th>
                    {series.map((item) => <th key={item.key} scope="col" className="right">{item.label}</th>)}
                    <th scope="col" className="right">{stackTotalLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((index) => (
                    <tr key={index}>
                      <th scope="row" className="num nowrap" style={{ fontWeight: 400 }}>{titles[index]}</th>
                      {series.map((item) => (
                        <td key={item.key} className="num right">{formatBRL(item.values[index] ?? 0)}</td>
                      ))}
                      <td className="num right" style={{ fontWeight: 500 }}>{formatBRL(stackTotal(index))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted chart-table-note">Pontos sem nenhum valor ficam de fora.</p>
          </>
        )}
      </details>
    </figure>
  );
}
